import {queueEvent, Event} from './events';
import {AppConfiguration, PakConfiguration, SatConfiguration} from './pineridge-channel';

// TODO - add circular buffer support
const MAX_LOGS = 5000;
export const logs: string[] = [];
let logIndex = 0;
let stamps = true;

export function setStamps(b: boolean) {
  stamps = b;
}

export function appLog(msg: string) {
  if (stamps) {
    const d = new Date();
    const h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();
    const ms = d.getMilliseconds();
    msg = `${h}:${m}:${s}.${ms} ${msg}`;
  }
  logs.push(msg);
  queueEvent(Event.LOG, msg, 0);
  log(msg);
  logIndex++;
  if (logIndex > MAX_LOGS) {
    clearLogs();
  }
}

export function appLogO(msg: string, o: unknown): void {
  const s = JSON.stringify(o, null, 2);
  const lines = s.split('\n');
  appLog(msg);
  lines.forEach(line => {
    appLog(line);
  });
}

export function log(...args: unknown[]): void {
  console.log(...args);
}

export function debug(...args: unknown[]): void {
  console.debug(...args);
}

export function clearLogs(): void {
  logs.length = 0;
  logIndex = 0;
}

export async function sleep(milliseconds: number) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

export function setVarInURL(key: string, value: string) {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.pushState({}, '', url.toString());
}

export function removeVarFromURL(key: string): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);
  window.history.pushState({}, '', url.toString());
}

export function isMobileBrowser(): boolean {
  const isMobile = /Mobile/i.test(navigator.userAgent);
  const isBrowser = typeof window !== 'undefined';
  return isMobile && isBrowser;
}

// Found this somewhere out on the internet many moons ago ...
export function JSFY(object: unknown): string | undefined {
  const cache: unknown[] = [];
  const str = JSON.stringify(
    object,
    (_key: string, value: unknown): unknown => {
      // custom replacer fxn - gets around "TypeError: Converting circular structure to JSON"
      if (typeof value === 'object' && value !== null) {
        if (cache.includes(value)) {
          // Circular reference found, discard key
          return;
        }
        // Store value in our collection
        cache.push(value);
      }
      return value;
    },
    4
  );

  return str;
}

function getObjectFromLocalStorage(key: string): unknown {
  const valueString = localStorage.getItem(key);
  if (valueString) {
    try {
      const value: unknown = JSON.parse(valueString);
      return value;
    } catch (err: unknown) {
      let errMessage = `Failed to parse localStorage value for key ${key}`;
      if (err instanceof Error) {
        errMessage += `: ${err.message}`;
      }

      appLog(errMessage);
      localStorage.removeItem(key);
      return null;
    }
  }
  return null;
}

function setObjectInLocalStorage(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function classNames(...classes: unknown[]) {
  return classes.filter(Boolean).join(' ');
}

export function getDefaultMediaDevice() {
  return {deviceId: 'default', label: 'Default'} as MediaDeviceInfo;
}

const APP_CONFIG_LOCAL_STORAGE_KEY = 'appConfig';

export function setAppConfigInLocalStorage(config: AppConfiguration) {
  setObjectInLocalStorage(APP_CONFIG_LOCAL_STORAGE_KEY, config);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isOptionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function isOptionalNumber(value: unknown): boolean {
  return value === undefined || typeof value === 'number';
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

export function isAppConfiguration(object: unknown): object is AppConfiguration {
  if (!isRecord(object) || !isPakConfiguration(object)) {
    return false;
  }

  return (
    isOptionalString(object.metadata) &&
    isOptionalBoolean(object.enableVideoByDefault) &&
    isOptionalString(object.userAgent) &&
    isOptionalString(object.mediaServiceUrl) &&
    isOptionalString(object.mediaServiceEnv)
  );
}

export function getAppConfigFromLocalStorage(): AppConfiguration {
  const localStorageValue = getObjectFromLocalStorage(APP_CONFIG_LOCAL_STORAGE_KEY);
  if (isAppConfiguration(localStorageValue)) {
    return localStorageValue;
  }

  return {
    pak: '',
    userId: '',
    channelId: '',
    allowPublishAudio: true,
    allowPublishVideo: true,
    allowPublishScreenShare: true,
    allowBotChat: true,
    allowVoiceDataCollection: true,
    tokenLifetimeSec: undefined,
    metadata: undefined,
    userAgent: undefined,
    mediaServiceUrl: undefined,
    mediaServiceEnv: undefined,
  };
}

export function isPakConfiguration(config: unknown): config is PakConfiguration {
  if (!isRecord(config)) {
    return false;
  }

  return (
    typeof config.pak === 'string' &&
    typeof config.userId === 'string' &&
    typeof config.channelId === 'string' &&
    typeof config.allowPublishAudio === 'boolean' &&
    typeof config.allowPublishVideo === 'boolean' &&
    typeof config.allowPublishScreenShare === 'boolean' &&
    typeof config.allowBotChat === 'boolean' &&
    typeof config.allowVoiceDataCollection === 'boolean' &&
    isOptionalNumber(config.tokenLifetimeSec)
  );
}

export function isSatConfiguration(config: unknown): config is SatConfiguration {
  return isRecord(config) && typeof config.sat === 'string';
}
